import type { ReactNode } from 'react';
import {
  Box,
  BrandLockup,
  Stack,
  Text,
  Tabs,
  IconButton,
  AddIcon,
  PhoneIcon,
  DeliveryIcon,
  LocationIcon,
  type TabItem,
} from '@citibox-panel/ui';

export interface SearchSummary {
  key: string;
  // string | undefined (not bare optional): matches the Zod-inferred SearchTab.label
  // so the full state objects can be passed straight through under exactOptionalPropertyTypes.
  label?: string | undefined;
  // Optional so every existing call site — and the literals that only ever build a phone
  // search — keeps compiling; the state model's required `kind` is still assignable to it.
  // An absent value therefore has to mean `user`, which is why the branches below ask for a
  // specific kind rather than for `!== 'user'`.
  kind?: 'user' | 'location' | 'delivery' | undefined;
}

export interface SearchShellHeaderProps {
  searches: SearchSummary[];
  activeKey: string | null;
  onSelect: (key: string) => void;
  onClose: (key: string) => void;
  onNewSearch: () => void;
  searchSlot?: ReactNode;
  headerEnd?: ReactNode;
  // string | undefined (not bare optional): the shell threads through the host-resolved
  // value, which is undefined until wired — pass-through needs exactOptionalPropertyTypes.
  brandLabel?: string | undefined;
  brandDegraded?: boolean | undefined;
}

function searchTabLabel(search: SearchSummary): ReactNode {
  const isLocation = search.kind === 'location';
  const isDelivery = search.kind === 'delivery';
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {isLocation ? (
        <LocationIcon fontSize="small" />
      ) : isDelivery ? (
        <DeliveryIcon fontSize="small" />
      ) : (
        <PhoneIcon fontSize="small" />
      )}
      {/* Width-capped, single-line and ellipsis-truncated — the same treatment the sub-tab pills
          give their labels, so an address (or a long name) never pushes "Cerrar" off the strip. */}
      <Box sx={{ textAlign: 'left', maxWidth: 160 }}>
        {/* Compact leading on both lines, so a two-line «Usuario» tab measures what a one-line
            location tab measures. The header is pinned, so its inset can only sit flush against one
            tab height — at the theme's own leading these two lines come to 39.9px and this tab
            outgrows the strip by 10px. Not a typographic preference: the stacked pair is exactly the
            label-over-value case `dense` exists for. */}
        <Text variant="body2" noWrap dense>
          {search.label ?? search.key}
        </Text>
        {/* The phone reads under the name because an agent can act on it. A location's key and a
            delivery's are opaque internal ids, so those labels stand alone rather than over
            plumbing nobody can act on. */}
        {!isLocation && !isDelivery && search.label !== undefined && (
          <Text variant="caption" color="secondary" dense>
            {search.key}
          </Text>
        )}
      </Box>
    </Stack>
  );
}

export function SearchShellHeader({
  searches,
  activeKey,
  onSelect,
  onClose,
  onNewSearch,
  searchSlot,
  headerEnd,
  brandLabel = 'Soporte',
  brandDegraded = false,
}: SearchShellHeaderProps) {
  const items: TabItem[] = searches.map((search) => ({
    value: search.key,
    closable: true,
    label: searchTabLabel(search),
  }));

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        // PINNED, not a minimum: the chrome is the one band on screen in every session, and a band
        // that changes height as tabs open and close is read as the page moving. The price is paid
        // by the tab labels below, which have to be one height for the strip's inset (in the Tabs
        // façade, solved against this exact number) to sit flush for every tab and not just one.
        height: 68,
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        // White chrome; the page below sits on the surface gray, so the active
        // session tab (which takes that gray) reads as pulled up from the content.
        bgcolor: 'background.paper',
        zIndex: 1100,
      }}
    >
      {/* TODO: i18n — brand wordmark stays literal. */}
      <BrandLockup label={brandLabel} degraded={brandDegraded} />
      {searches.length > 0 && activeKey !== null && (
        <Tabs
          items={items}
          value={activeKey}
          onChange={onSelect}
          variant="enclosed"
          onClose={onClose}
        />
      )}
      <IconButton aria-label="Nueva búsqueda" onClick={onNewSearch}>
        <AddIcon />
      </IconButton>
      <Box sx={{ flexGrow: 1 }} />
      {/* The slot is a flex row with a width, not a plain box: `HeaderSearch` returns a fragment, so
          its field and its dialog land here as siblings, and a block wrapper would stack them. (Its
          live region used to land here too, until CBX-11386 made that the shell host's — the slot is
          a row for the remaining two.) `position: relative` is what lets the phone hint hang under
          the field without pushing the chrome down. 360px is solved for the placeholder fitting
          whole. */}
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', width: 360 }}>
        {searchSlot}
      </Box>
      {headerEnd}
    </Box>
  );
}
